import { LitElement, html, css, unsafeCSS } from 'lit';
import leafletCss from 'leaflet/dist/leaflet.css?inline';
import L from 'leaflet';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { X, Layers, TestTube, PaintBucket, FlaskConical, Droplets, Image as ImageIcon, Mic, FileText, ChevronRight, Plus, Check } from 'lucide';
import { lucideIcon } from '../icon.js';
import { convertFromWgs84, convertToWgs84, COORDINATE_SYSTEMS } from '@mv/models';
import { api } from '../api.js';

const MAPTILER_KEY = import.meta.env.VITE_MAPTILER_KEY || '';
const TILE_WORLD = `https://api.maptiler.com/maps/streets-v2/256/{z}/{x}/{y}.png?key=${MAPTILER_KEY}`;
const TILE_WORLD_AERIAL = `https://api.maptiler.com/maps/satellite/256/{z}/{x}/{y}.jpg?key=${MAPTILER_KEY}`;
const TILE_BRT = 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/standaard/EPSG:3857/{z}/{x}/{y}.png';
const ATTR = '&copy; <a href="https://www.maptiler.com/" target="_blank" rel="noopener">MapTiler</a> &copy; OpenStreetMap · PDOK';
/** PDOK-BRT alleen binnen Nederland ophalen — buiten die rechthoek bestaan de tegels niet. */
const NL_BOUNDS = [[50.5, 3.0], [53.8, 7.5]];
/** Midden van Nederland — alleen als terugval wanneer er niets is om op te zoomen. */
const NL_CENTER = [52.1, 5.3];

/**
 * boring-duplicate-dialog — een boring nog een keer, ergens anders.
 *
 * In een sleuf of op een terrein staan boringen die op elkaar lijken. Die tien keer
 * overtikken is werk dat de computer hoort te doen.
 *
 * Twee stappen, en die volgorde is niet willekeurig. Eerst NAAM en opmerking: dat is wat je
 * al in je hoofd hebt op het moment dat je op dupliceren klikt. Daarna pas de LOCATIE, want
 * daarvoor moet je de kaart lezen en dat is een ander soort aandacht.
 *
 * De voortgang is een balk tussen twee labels, geen kolom links: die kostte 200px breedte
 * om twee regels te tonen.
 *
 * Wat er meegaat staat in stap 1 als AANVINKBARE regels — soms wil je de lagen wel en de
 * peilbuis niet. Alleen de lagen liggen vast; zonder lagen is het geen kopie van een boring.
 *
 * Wat er NIET meegaat zit onder 'Blijft leeg', ingeklapt. Wie het vertrouwt hoeft het niet
 * te lezen, wie twijfelt vindt het precies daar. Weglaten was geen optie: ontdek je pas
 * achteraf dat meetwaarden zijn meegekopieerd, dan staan er verzonnen cijfers in je rapport
 * die niet meer van echte te onderscheiden zijn.
 */
export class BoringDuplicateDialog extends LitElement {
  static properties = {
    boring: { type: Object },       // de bron; null = dicht
    coordSystem: { type: String },  // voor de weergave van het geprikte punt
    _step: { state: true },
    _name: { state: true },
    _opmerking: { state: true },
    _latlng: { state: true },
    _base: { state: true },        // 'map' | 'aerial'
    _include: { state: true },     // wat er meegaat; lagen staan er niet in (altijd)
    _showEmpty: { state: true },   // 'blijft leeg' uitgeklapt?
    _showRemark: { state: true },
    _xText: { state: true },       // ruwe invoer, zodat half getypte getallen blijven staan
    _yText: { state: true },
    _z: { state: true },           // maaiveldhoogte NAP (m)
    _coordError: { state: true },
    _busy: { state: true },
    _error: { state: true },
  };

  constructor() {
    super();
    this.boring = null;
    this.coordSystem = 'WGS84';
    this._reset();
  }

  _reset() {
    this._step = 1;
    this._name = '';
    this._opmerking = '';
    this._latlng = null;
    // Luchtfoto als startweergave: bij het prikken van een boorpunt herken je het terrein
    // (bosrand, sloot, verharding), niet de straatnaam.
    this._base = 'aerial';
    this._include = { peilbuizen: true, afwerking: true, header: true };
    this._showEmpty = false;
    this._showRemark = false;
    this._xText = '';
    this._yText = '';
    this._z = '';
    this._coordError = null;
    this._busy = false;
    this._error = null;
  }

  updated(changed) {
    if (changed.has('boring')) {
      if (this.boring) {
        this._reset();
        this._name = `${this.boring.name || ''} (kopie)`;
        this._opmerking = this.boring.opmerking || '';
        // Had het origineel een opmerking, dan staat het veld meteen open: die tekst
        // verstoppen achter een knop zou hem stilletjes meekopiëren.
        this._showRemark = !!this.boring.opmerking;
        // De hoogte van de bron als vertrekpunt. Vrijwel altijd bijna goed op een paar meter
        // afstand, en de gebruiker ziet 'm staan en kan hem aanpassen — beter dan leeg, want
        // dan komt er meestal niets in te staan.
        this._z = this.boring.z != null ? String(this.boring.z) : '';
      } else {
        this._destroyMap();
      }
    }
    // De kaart pas opbouwen als hij ook echt in beeld staat: Leaflet meet zijn eigen
    // container, en in een verborgen element is die nul bij nul.
    if (this.boring && this._step === 2 && !this._map) this._initMap();
    if (this._step !== 2 && this._map) this._destroyMap();
  }

  disconnectedCallback() {
    this._destroyMap();
    super.disconnectedCallback();
  }

  _destroyMap() {
    if (this._map) { this._map.remove(); this._map = null; this._marker = null; }
  }

  _initMap() {
    const el = this.renderRoot.querySelector('#dupmap');
    if (!el) return;
    // Rond de bronboring beginnen als die een locatie heeft: een kopie hoort er vrijwel
    // altijd vlakbij. Anders vallen we terug op Nederland.
    const src = this._sourceLatLng();
    this._map = L.map(el, { center: src || NL_CENTER, zoom: src ? 18 : 7, zoomControl: true, attributionControl: true });

    // Zelfde twee weergaven als de kaart-tab: 'Kaart' = wereldkaart met PDOK-BRT bovenop
    // voor scherp NL-detail, 'Luchtfoto' = alleen de satelliet. Bij het prikken van een punt
    // is de luchtfoto vaak de bruikbaarste: je herkent het terrein, niet de straatnaam.
    this._tiles = {
      map: [
        L.tileLayer(TILE_WORLD, { maxZoom: 21, attribution: ATTR }),
        L.tileLayer(TILE_BRT, { bounds: L.latLngBounds(NL_BOUNDS), maxZoom: 21, maxNativeZoom: 19 }),
      ],
      aerial: [
        L.tileLayer(TILE_WORLD_AERIAL, { maxZoom: 21, maxNativeZoom: 20, attribution: ATTR }),
      ],
    };
    this._addBase(this._base);

    if (src) {
      // De bron als open cirkel: je ziet waar je vandaan komt zonder hem met je nieuwe
      // punt te verwarren.
      L.circleMarker(src, { radius: 7, color: '#6b7280', weight: 2, fillOpacity: 0 })
        .addTo(this._map)
        .bindTooltip(this.boring?.name || 'origineel', { direction: 'top' });
    }
    this._map.on('click', (e) => this._place(e.latlng));
    // Leaflet meet verkeerd als de container net zichtbaar werd.
    setTimeout(() => this._map && this._map.invalidateSize(), 60);
  }

  _addBase(kind) { for (const l of this._tiles?.[kind] || []) l.addTo(this._map); }
  _removeBase(kind) { for (const l of this._tiles?.[kind] || []) l.remove(); }
  _setBase(kind) {
    if (kind === this._base) return;
    if (this._map) { this._removeBase(this._base); this._addBase(kind); }
    this._base = kind;
  }

  _sourceLatLng() {
    const b = this.boring;
    if (!b || b.y == null || b.x == null) return null;
    return [Number(b.y), Number(b.x)];
  }

  /**
   * De speld. Bewust een `divIcon` en niet Leaflets standaardmarker: die laadt een PNG via
   * een relatief pad dat de bundler niet meeneemt, wat een kapot plaatje met de tekst
   * 'Marker' oplevert. Een gekleurde stip is bovendien dezelfde taal als de kaart-tab.
   */
  _pinIcon() {
    return L.divIcon({
      className: '',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
      html: '<div style="width:16px;height:16px;border-radius:50%;background:#4b9d35;border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.45)"></div>',
    });
  }

  _place(latlng, { syncFields = true } = {}) {
    this._latlng = latlng;
    this._coordError = null;
    if (this._marker) this._marker.setLatLng(latlng);
    else this._marker = L.marker(latlng, { icon: this._pinIcon() }).addTo(this._map);
    // Klikken op de kaart schrijft de velden bij; typen doet het omgekeerde. Zo is er één
    // waarheid en zie je meteen welke coördinaat je te pakken hebt.
    if (syncFields) {
      const p = this._project(latlng);
      this._xText = this._fmt(p.x);
      this._yText = this._fmt(p.y);
    }
  }

  /** Het punt in het stelsel van het project (RD, UTM of WGS84). */
  _project(latlng) {
    const sys = this.coordSystem || 'WGS84';
    return convertFromWgs84(latlng.lng, latlng.lat, sys) || { x: latlng.lng, y: latlng.lat };
  }

  _isDegrees() { return (this.coordSystem || 'WGS84') === 'WGS84'; }

  _fmt(n) {
    return this._isDegrees()
      ? Number(n).toFixed(6)
      : String(Math.round(Number(n)));
  }

  /**
   * Getypte coördinaten → kaart. Pas als beide velden een getal bevatten; halverwege het
   * typen van een RD-coördinaat zou de kaart anders naar de Noordzee springen.
   */
  _applyTypedCoords() {
    const x = Number(String(this._xText).replace(',', '.'));
    const y = Number(String(this._yText).replace(',', '.'));
    if (!Number.isFinite(x) || !Number.isFinite(y)) { this._coordError = null; return; }
    const wgs = convertToWgs84(x, y, this.coordSystem || 'WGS84');
    if (!wgs) { this._coordError = 'Deze coördinaat ligt buiten het gebied van dit stelsel.'; return; }
    const latlng = L.latLng(wgs.latitude, wgs.longitude);
    this._place(latlng, { syncFields: false });
    if (this._map) this._map.setView(latlng, Math.max(this._map.getZoom(), 17));
  }

  _toggle(key) { this._include = { ...this._include, [key]: !this._include[key] }; }

  /**
   * Eén regel in 'Gaat mee'. `key` null = vast aan (de lagen); dan is de rij geen knop, want
   * een vinkje dat niet uit kan hoort er ook niet uit te zien alsof dat wel kan.
   */
  _pick(key, icon, label, detail = '', badge = '') {
    const on = key ? !!this._include[key] : true;
    const fixed = !key;
    const body = html`
      <span class="box ${on ? 'on' : ''} ${fixed ? 'fixed' : ''}">${on ? unsafeSVG(lucideIcon(Check, 13)) : ''}</span>
      <span class="ico">${unsafeSVG(lucideIcon(icon, 15))}</span>
      <span class="txt"><b>${label}</b>${detail ? html` <em>${detail}</em>` : ''}</span>
      ${badge ? html`<span class="badge">${badge}</span>` : ''}`;
    return fixed
      ? html`<div class="pick on fixed">${body}</div>`
      : html`<button type="button" class="pick ${on ? 'on' : ''}" @click=${() => this._toggle(key)}>${body}</button>`;
  }

  /** Eén regel in 'Blijft leeg' — leesbaar, maar zichtbaar niet aan te zetten. */
  _empty(icon, label, detail = '') {
    return html`
      <div class="pick off">
        <span class="box dashed"></span>
        <span class="ico">${unsafeSVG(lucideIcon(icon, 15))}</span>
        <span class="txt"><b>${label}</b>${detail ? html` <em>${detail}</em>` : ''}</span>
      </div>`;
  }

  /** Terug mag altijd; vooruit pas als de naam is ingevuld — zoals bij 'Nieuw project'. */
  _goToStep(n) {
    if (n > this._step && !(this._name || '').trim()) {
      this._error = 'Geef de nieuwe boring een naam.';
      return;
    }
    this._error = null;
    this._step = Math.min(Math.max(n, 1), 2);
  }

  async _submit() {
    const name = (this._name || '').trim();
    if (!name) { this._error = 'Geef de nieuwe boring een naam.'; this._step = 1; return; }
    this._busy = true; this._error = null;
    try {
      const created = await api.duplicateBoring(this.boring.id, {
        name,
        opmerking: this._opmerking.trim() || null,
        x: this._latlng ? this._latlng.lng : null,
        y: this._latlng ? this._latlng.lat : null,
        z: this._z !== '' && Number.isFinite(Number(String(this._z).replace(',', '.')))
          ? Number(String(this._z).replace(',', '.'))
          : null,
        include: this._include,
        sourceName: this.boring.name ?? null,
      });
      this.dispatchEvent(new CustomEvent('duplicated', { detail: { boring: created }, bubbles: true, composed: true }));
      this._close();
    } catch (e) {
      this._error = e?.message || 'Dupliceren mislukt.';
    } finally {
      this._busy = false;
    }
  }

  _close() {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
  }

  render() {
    if (!this.boring) return '';
    const b = this.boring;
    const nLagen = (b.layers || []).length;
    const nPb = (b.peilbuizen || []).length;
    const nAfw = (b.afwerking?.fillings || []).length;
    const nMonsters = (b.monsters || []).length;
    const nWater = (b.watermonsters || []).length;
    const nFoto = (b.photos || []).length;
    const sys = COORDINATE_SYSTEMS[this.coordSystem] || COORDINATE_SYSTEMS.WGS84;
    // 'RD X' / 'WGS84 Lon' — de volledige stelselnaam ('RD (Rijksdriehoek)') maakt het
    // labeltje boven een smal veld onleesbaar.
    const axisLabel = (i) => `${sys.id} ${sys.axes[i]}`;

    return html`
      <div class="back" @click=${(e) => { if (e.target === e.currentTarget && !this._busy) this._close(); }}>
        <div class="modal">
          <div class="head">
            <div>
              <h3>Boring dupliceren</h3>
              <span class="sub">${b.name} · stap ${this._step} van 2 — ${this._step === 1 ? 'naam & inhoud' : 'locatie'}</span>
            </div>
            <button class="x" @click=${() => this._close()} ?disabled=${this._busy}>${unsafeSVG(lucideIcon(X, 18))}</button>
          </div>

          ${this._error ? html`<div class="err">${this._error}</div>` : ''}

          <div class="body">
          ${this._step === 1 ? html`
            <label class="fld">
              <span>Naam van de nieuwe boring</span>
              <input type="text" .value=${this._name} autofocus
                @input=${(e) => { this._name = e.target.value; }}>
            </label>

            <!-- Vinkjes in plaats van een opsomming: soms wil je de lagen wel en de peilbuis
                 niet. Lagen staan vast — zonder lagen is het geen kopie van een boring maar
                 een leeg boorgat, en daar is 'Nieuwe boring' voor. -->
            <div class="sec-head">
              <h4>Gaat mee</h4>
              <span>vink uit wat je niet wilt</span>
            </div>
            <div class="picks">
              ${this._pick(null, Layers, `${nLagen} ${nLagen === 1 ? 'laag' : 'lagen'}`, '', 'altijd')}
              ${nPb ? this._pick('peilbuizen', TestTube, `${nPb} ${nPb === 1 ? 'peilbuis' : 'peilbuizen'}`, 'alleen de constructie') : ''}
              ${nAfw ? this._pick('afwerking', PaintBucket, 'Afwerking', `${nAfw} traject${nAfw === 1 ? '' : 'en'}`) : ''}
              ${this._pick('header', FileText, 'Kopgegevens', 'type, boormeester, referentievlak')}
            </div>

            <!-- Ingeklapt: wie het vertrouwt hoeft het niet te lezen, wie twijfelt vindt het
                 precies hier. Wat er NIET meegaat is de belangrijkste helft — ontdek je dat
                 pas achteraf, dan staan er verzonnen meetwaarden in je rapport. -->
            <button class="disclose ${this._showEmpty ? 'open' : ''}" @click=${() => { this._showEmpty = !this._showEmpty; }}>
              ${unsafeSVG(lucideIcon(ChevronRight, 15))}
              <b>Blijft leeg</b>
              <span>hoort bij één boring</span>
            </button>
            ${this._showEmpty ? html`
              <div class="picks empty">
                ${this._empty(FlaskConical, `${nMonsters} grondmonster${nMonsters === 1 ? '' : 's'}`)}
                ${this._empty(Droplets, `${nWater} watermonster${nWater === 1 ? '' : 's'}`)}
                ${this._empty(TestTube, 'Peilbuismetingen', 'pH, EC, temperatuur, doorloop')}
                ${this._empty(ImageIcon, `${nFoto} foto${nFoto === 1 ? '' : "'s"}`)}
                ${this._empty(Mic, 'Opname en transcript')}
              </div>
              <p class="note">
                Deze velden zijn uniek voor een boring en kun je zelf invullen na het dupliceren van boring.
              </p>` : ''}

            ${this._showRemark ? html`
              <label class="fld">
                <span>Opmerking <em>optioneel</em></span>
                <textarea rows="3" placeholder="Bijvoorbeeld: identiek profiel, 5 m noordelijker"
                  .value=${this._opmerking}
                  @input=${(e) => { this._opmerking = e.target.value; }}></textarea>
                ${b.opmerking ? html`<small>Overgenomen van ${b.name}; pas aan wat niet meer klopt.</small>` : ''}
              </label>`
              : html`
              <button class="addlink" @click=${() => { this._showRemark = true; }}>
                ${unsafeSVG(lucideIcon(Plus, 14))} Opmerking toevoegen
              </button>`}
          ` : html`
            <p class="lead">
              Klik op de kaart om de nieuwe boring te plaatsen.
              ${this._sourceLatLng() ? html`De cirkel is ${b.name}.` : html`${b.name} heeft zelf geen coördinaten, dus de kaart begint op Nederland.`}
            </p>
            <div class="map-wrap">
              <div class="seg">
                <button class=${this._base === 'map' ? 'on' : ''} @click=${() => this._setBase('map')}>Kaart</button>
                <button class=${this._base === 'aerial' ? 'on' : ''} @click=${() => this._setBase('aerial')}>Luchtfoto</button>
              </div>
              <div id="dupmap" class="map"></div>
            </div>
            <!-- Niet alleen een uitlezing maar invoervelden: een uitgezette boring komt
                 vaak als coördinaat uit een meetstaat binnen, en dan is intikken exacter
                 dan klikken. Beide richtingen werken op dezelfde twee velden. -->
            <div class="coords">
              <label class="cfld">
                <span>${axisLabel(0)}</span>
                <input inputmode="decimal" placeholder=${this._isDegrees() ? '5.123456' : '155000'}
                  .value=${this._xText}
                  @input=${(e) => { this._xText = e.target.value; }}
                  @change=${() => this._applyTypedCoords()}
                  @keydown=${(e) => { if (e.key === 'Enter') this._applyTypedCoords(); }}>
              </label>
              <label class="cfld">
                <span>${axisLabel(1)}</span>
                <input inputmode="decimal" placeholder=${this._isDegrees() ? '52.123456' : '463000'}
                  .value=${this._yText}
                  @input=${(e) => { this._yText = e.target.value; }}
                  @change=${() => this._applyTypedCoords()}
                  @keydown=${(e) => { if (e.key === 'Enter') this._applyTypedCoords(); }}>
              </label>
              <label class="cfld">
                <span>Maaiveld (m NAP)</span>
                <input inputmode="decimal" placeholder="bijv. 1,25"
                  .value=${this._z}
                  @input=${(e) => { this._z = e.target.value; }}>
              </label>
            </div>
            ${this._coordError ? html`<p class="note warn">${this._coordError}</p>` : ''}
            ${!this._latlng ? html`
              <p class="note">Nog geen locatie gekozen — dat mag ook later, via de kaart-tab.</p>` : ''}
          `}
          </div>

          <div class="foot">
            <button class="btn ghost" @click=${() => this._close()} ?disabled=${this._busy}>Annuleren</button>
            <div class="foot-right">
              ${this._step > 1 ? html`<button class="btn ghost" @click=${() => { this._step = 1; }} ?disabled=${this._busy}>Vorige</button>` : ''}
              ${this._step === 1
                ? html`<button class="btn" @click=${() => this._goToStep(2)}>Volgende</button>`
                : html`<button class="btn" ?disabled=${this._busy} @click=${() => this._submit()}>
                    ${this._busy ? 'Dupliceren…' : this._latlng ? 'Dupliceren' : 'Dupliceren zonder locatie'}
                  </button>`}
            </div>
          </div>
        </div>
      </div>`;
  }

  static styles = css`
    ${unsafeCSS(leafletCss)}
    :host { display: contents; }
    .back { position: fixed; inset: 0; z-index: var(--mv-z-modal, 1200); background: rgba(15, 23, 42, .45);
      display: flex; align-items: center; justify-content: center; padding: 4vh 1rem; }
    /* Geen stapkolom links: die kostte 200px breedte terwijl de kop hetzelfde kan zeggen.
       De lijstjes staan nu onder elkaar als aanvinkbare regels, dus het venster hoeft ook
       niet extra breed — smal leest een lijst juist prettiger. */
    .modal { width: min(620px, 100%); max-height: 92vh; background: var(--mv-surface); border-radius: .8rem;
      box-shadow: 0 24px 60px rgba(0,0,0,.25); display: flex; flex-direction: column; overflow: hidden; }

    .head { display: flex; align-items: flex-start; gap: 12px; padding: 26px 28px 0; }
    .head h3 { margin: 0; font-size: 1rem; font-weight: 800; color: var(--mv-text); }
    .head .sub { font-size: .8rem; color: var(--mv-text-muted); }
    .x { margin-left: auto; display: inline-flex; background: none; border: none; cursor: pointer; color: var(--mv-text-sub); padding: 4px; }

    .progress { display: flex; align-items: center; gap: 12px; padding: 14px 28px 0; }
    .pstep { background: none; border: none; padding: 0; font: inherit; font-size: .8rem;
      font-weight: 600; color: var(--mv-text-muted); cursor: pointer; white-space: nowrap; }
    .pstep.on { color: var(--mv-primary); font-weight: 700; }
    .ptrack { flex: 1; height: 3px; border-radius: 2px; background: var(--mv-border); overflow: hidden; }
    .pfill { display: block; height: 100%; background: var(--mv-primary); transition: width .2s ease; }

    .err { margin: .9rem 28px 0; padding: 8px 11px; border-radius: 7px; font-size: .82rem;
      color: var(--mv-danger, #B91C1C); background: var(--mv-danger-bg, #FEE2E2); }

    .body { padding: 18px 28px; display: flex; flex-direction: column; gap: 14px; overflow-y: auto; }
    .fld { display: flex; flex-direction: column; gap: 5px; }
    .fld span { font-size: .78rem; font-weight: 600; color: var(--mv-text-sub); }
    .fld em { font-style: normal; font-weight: 500; color: var(--mv-text-muted); }
    .fld small { font-size: .72rem; color: var(--mv-text-muted); }
    .fld input, .fld textarea { padding: 8px 10px; border: 1px solid var(--mv-border); border-radius: 7px;
      font: inherit; font-size: .86rem; background: var(--mv-surface); color: var(--mv-text); }
    .fld textarea { min-height: 66px; resize: vertical; line-height: 1.45; }
    .fld input:focus, .fld textarea:focus { outline: none; border-color: var(--mv-primary);
      box-shadow: 0 0 0 3px var(--mv-primary-soft, rgba(34,139,76,.16)); }

    .sec-head { display: flex; align-items: baseline; gap: 10px; margin-top: 4px; }
    .sec-head h4 { margin: 0; font-size: .84rem; font-weight: 800; color: var(--mv-text); }
    .sec-head span { margin-left: auto; font-size: .74rem; color: var(--mv-text-muted); }

    .picks { display: flex; flex-direction: column; gap: 6px; }
    /* box-sizing expliciet: in de shadow DOM geldt de globale reset niet, en zonder dit is
       een <button>-rij (content-box + padding) net iets anders breed dan een <div>-rij. */
    .pick { display: flex; align-items: center; gap: 10px; box-sizing: border-box; text-align: left;
      padding: 8px 11px; border: 1px solid var(--mv-border); border-radius: 9px;
      background: var(--mv-surface); font: inherit; cursor: pointer; }
    .pick.fixed, .pick.off { cursor: default; }
    .pick.on { border-color: var(--mv-primary); background: var(--mv-primary-tint, rgba(75,157,53,.06)); }
    .pick.off { background: var(--mv-surface-alt, #f9fafb); }
    .box { width: 18px; height: 18px; flex: 0 0 18px; border-radius: 5px; display: inline-flex;
      align-items: center; justify-content: center; color: #fff;
      border: 1.5px solid var(--mv-border-strong, #cfd8dc); background: var(--mv-surface); }
    .box.on { background: var(--mv-primary); border-color: var(--mv-primary); }
    /* Vast aan: wel afgevinkt, maar gedempt zodat het niet uitnodigt tot klikken. */
    .box.fixed { opacity: .55; }
    .box.dashed { border-style: dashed; background: none; }
    .ico { display: inline-flex; color: var(--mv-text-muted); }
    .pick.on .ico { color: var(--mv-primary); }
    .txt { flex: 1; min-width: 0; font-size: .83rem; color: var(--mv-text); }
    .txt b { font-weight: 700; }
    .txt em { font-style: normal; color: var(--mv-text-muted); }
    .pick.off .txt { color: var(--mv-text-muted); }
    .badge { font-size: .74rem; color: var(--mv-text-muted); }

    .disclose { display: flex; align-items: center; gap: 8px; width: 100%; padding: 6px 0;
      background: none; border: none; font: inherit; cursor: pointer; color: var(--mv-text); }
    .disclose b { font-size: .84rem; font-weight: 800; }
    .disclose span { margin-left: auto; font-size: .74rem; color: var(--mv-text-muted); }
    .disclose svg { transition: transform .15s ease; color: var(--mv-text-muted); }
    .disclose.open svg { transform: rotate(90deg); }

    .note { margin: 0; font-size: .78rem; line-height: 1.5; color: var(--mv-text-muted); }
    .addlink { display: inline-flex; align-items: center; gap: 6px; align-self: flex-start;
      background: none; border: none; padding: 4px 0; font: inherit; font-size: .82rem;
      font-weight: 700; color: var(--mv-primary); cursor: pointer; }

    .lead { margin: 0; font-size: .82rem; color: var(--mv-text-sub); line-height: 1.45; }
    .map-wrap { position: relative; }
    .map { height: 360px; border-radius: 9px; overflow: hidden; border: 1px solid var(--mv-border); }
    /* Zelfde schakelaar als op de kaart-tab, zodat hij op beide plekken hetzelfde bedient. */
    .seg { position: absolute; top: 10px; right: 10px; z-index: 500; display: flex; background: var(--mv-surface);
      border: 1px solid var(--mv-border); border-radius: 7px; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,.12); }
    .seg button { border: none; background: none; padding: 6px 12px; font: inherit; font-size: .76rem;
      font-weight: 600; color: var(--mv-text-muted); cursor: pointer; }
    .seg button.on { background: var(--mv-primary); color: #fff; }
    .coords { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
    @media (max-width: 560px) { .coords { grid-template-columns: 1fr 1fr; } }
    .cfld { display: flex; flex-direction: column; gap: 4px; }
    .cfld span { font-size: .72rem; font-weight: 700; color: var(--mv-text-sub); }
    .cfld input { padding: 8px 10px; border: 1px solid var(--mv-border); border-radius: 7px;
      font: inherit; font-size: .84rem; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      background: var(--mv-surface); color: var(--mv-text); }
    .cfld input:focus { outline: none; border-color: var(--mv-primary);
      box-shadow: 0 0 0 3px var(--mv-primary-soft, rgba(34,139,76,.16)); }
    .note.warn { color: var(--mv-danger, #B91C1C); }

    /* Annuleren links, stapknoppen rechts — net als het projectformulier. */
    .foot { display: flex; justify-content: space-between; align-items: center; gap: 8px; padding: 14px 28px 26px; }
    .foot-right { display: flex; gap: 8px; }
    .btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 15px; border: none; border-radius: 8px;
      background: var(--mv-primary); color: #fff; font: inherit; font-size: .82rem; font-weight: 700; cursor: pointer; }
    .btn:disabled { opacity: .55; cursor: default; }
    .btn.ghost { background: none; color: var(--mv-text-sub); border: 1px solid var(--mv-border); }
  `;
}

customElements.define('boring-duplicate-dialog', BoringDuplicateDialog);
